// Google Sheets data fetcher + normalizer (SIMPLIFIED).
// Sheet must be published to web as CSV. URL stored in /config.json.
//
// SIMPLIFIED 12-column schema (header row, case-insensitive):
//   id, type, projectName, listingName, bedrooms, priceRange,
//   location, size, image, brochure, pricing, status
//
// type    = "project" or "listing". For listings, projectName = parent project name.
// image   = filename like "brigade.jpg" → resolved to "/images/brigade.jpg"
//           (multiple files separated by commas are supported).
// brochure / pricing = filename → "/brochures/<file>" and "/pricing/<file>".
// status  = "active" | "sold" | "hidden"   (hidden rows are filtered out)
//
// Backwards-compat: legacy column names (imageUrl, brochureUrl, pricingUrl,
// startingPrice, endingPrice, configuration, etc.) are still accepted so
// existing sheets keep working.

import { getConfig } from './runtimeConfig';

const FALLBACK_IMAGE =
  'https://images.pexels.com/photos/3280078/pexels-photo-3280078.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

// --- CSV parsing (RFC4180-ish: quoted strings, escaped quotes, newlines) ---
const parseCSV = (text) => {
  const rows = [];
  let row = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cur);
        cur = '';
      } else if (ch === '\n') {
        row.push(cur);
        rows.push(row);
        row = [];
        cur = '';
      } else if (ch === '\r') {
        // ignore
      } else {
        cur += ch;
      }
    }
  }
  if (cur.length > 0 || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }
  return rows;
};

const headerKey = (h) =>
  String(h || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

// Resolve a media value to a URL.
//   "brigade.jpg"                        → "/images/brigade.jpg"
//   "https://...image.jpg"               → kept as-is (external)
//   "/images/already-rooted.jpg"         → kept as-is
//   ""                                   → ""
// `folder` is "images" | "brochures" | "pricing".
const resolveAsset = (raw, folder) => {
  if (!raw) return '';
  const v = String(raw).trim();
  if (!v) return '';
  // External URL — keep as-is (graceful for legacy sheets).
  if (/^https?:\/\//i.test(v)) return v;
  // Already an absolute server path — keep as-is.
  if (v.startsWith('/')) return v;
  // Drive link (legacy) — convert to uc URL.
  if (/drive\.google\.com/i.test(v)) {
    const m = v.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || v.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (m) return `https://drive.google.com/uc?export=view&id=${m[1]}`;
    return v;
  }
  // Plain filename → resolve under the folder.
  return `/${folder}/${v.replace(/^\/+/, '')}`;
};

// Split a multi-image cell ("a.jpg, b.jpg") into resolved URLs.
const parseImages = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(/[,\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((u) => resolveAsset(u, 'images'));
};

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 80) || 'item';

// Map raw status strings to the UI's display value.
//   "active" / "available"     → "Available"
//   "sold"                     → "Sold"
//   "hidden" / "draft"         → "Hidden" (caller filters these out)
const normalizeStatus = (raw) => {
  const s = String(raw || '').toLowerCase().trim();
  if (s === 'hidden' || s === 'draft' || s === 'private') return 'Hidden';
  if (s === 'sold') return 'Sold';
  return 'Available';
};

// Normalize one CSV row → property object compatible with existing UI.
const normalize = (row, idx) => {
  const get = (...keys) => {
    for (const k of keys) {
      if (row[k] !== undefined && row[k] !== '') return row[k];
    }
    return '';
  };

  const type = String(get('type', 'contenttype') || '').toLowerCase().trim();
  const isProject = type === 'project';
  const projectName = get('projectname', 'project');
  const listingName = get('listingname', 'listing');
  const titleRaw = isProject ? projectName || listingName : listingName || projectName;
  const title = titleRaw || `Item ${idx + 1}`;

  const idVal = get('id') || `${slugify(title)}-${idx + 1}`;

  // Images: prefer simplified `image`, fall back to legacy `imageUrl`.
  const imagesRaw = get('image', 'images', 'imageurl');
  const images = parseImages(imagesRaw);

  // Brochure / pricing: simplified filename or legacy URL.
  const brochureRaw = get('brochure', 'brochureurl');
  const pricingRaw = get('pricing', 'pricingurl');

  // ---- Price (prefer priceRange, fall back to legacy fields) ------------
  const rangeStr = get('pricerange', 'pricingrange');
  const startPrice = get('startingprice', 'startprice', 'pricestart');
  const endPrice = get('endingprice', 'endprice', 'priceend');
  const singlePrice = get('price');

  let computedPrice = '';
  if (rangeStr) {
    computedPrice = rangeStr;
  } else if (startPrice && endPrice) {
    computedPrice = `${startPrice} – ${endPrice}`;
  } else if (startPrice) {
    computedPrice = `${startPrice} onwards`;
  } else if (singlePrice) {
    computedPrice = singlePrice;
  }

  // ---- Bedrooms / configuration --------------------------------------
  const bedroomsRaw = get('bedrooms');
  const configRaw = get('configuration');
  let bedroomsDisplay = '';
  if (bedroomsRaw) {
    const trimmed = String(bedroomsRaw).trim();
    bedroomsDisplay = /bhk/i.test(trimmed) ? trimmed : `${trimmed} BHK`;
  } else if (configRaw && /bhk/i.test(configRaw)) {
    bedroomsDisplay = configRaw;
  }

  const status = normalizeStatus(get('status'));

  return {
    id: idVal,
    content_type: isProject ? 'project' : 'listing',
    title,
    listing_name: listingName || '',
    project_name: projectName || '',
    builder: get('builder') || (isProject ? '' : projectName) || '',
    location: get('location') || '',
    price: computedPrice,
    starting_price: startPrice || '',
    ending_price: endPrice || '',
    configuration: configRaw || '',
    size: get('size') || '',
    bedrooms: bedroomsDisplay,
    area: get('area') || get('size') || '',
    property_type: get('propertytype', 'type2') || (isProject ? 'Project' : 'Apartment'),
    description: get('description') || '',
    amenities: get('amenities') || '',
    status,
    raw_status: String(get('status') || '').toLowerCase().trim(),
    featured: /^(true|yes|1)$/i.test(String(get('featured') || '')),
    images,
    image_url_raw: imagesRaw || '',
    brochure_url: resolveAsset(brochureRaw, 'brochures'),
    pricing_pdf_url: resolveAsset(pricingRaw, 'pricing'),
    video_url: get('videourl', 'video') || '',
    pricing_range: rangeStr || '',
    connectivity: get('connectivity') || '',
    land_area: get('landarea') || '',
    units: get('units') || '',
    carpet_area: get('carpetarea') || '',
    balcony_area: get('balconyarea') || '',
  };
};

let propertiesCache = null;
let propertiesPromise = null;

export const fetchProperties = async ({ force = false } = {}) => {
  if (!force && propertiesCache) return propertiesCache;
  if (!force && propertiesPromise) return propertiesPromise;

  propertiesPromise = (async () => {
    const cfg = await getConfig();
    const url = cfg.GOOGLE_SHEET_CSV_URL;
    if (!url) {
      propertiesCache = [];
      return propertiesCache;
    }
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      const rows = parseCSV(text).filter((r) => r.some((c) => String(c).trim() !== ''));
      if (rows.length < 2) {
        propertiesCache = [];
        return propertiesCache;
      }
      const headers = rows[0].map(headerKey);
      const data = rows.slice(1).map((cells) => {
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = (cells[i] || '').trim();
        });
        return obj;
      });
      propertiesCache = data
        .filter((r) => r.projectname || r.listingname || r.title)
        .map((r, i) => normalize(r, i))
        // Hide rows explicitly marked hidden/draft/private.
        .filter((p) => p.status !== 'Hidden');
      return propertiesCache;
    } catch (err) {
      console.error('Failed to load Google Sheet:', err);
      propertiesCache = [];
      return propertiesCache;
    }
  })();
  return propertiesPromise;
};

export const findProperty = async (id) => {
  const all = await fetchProperties();
  return all.find((p) => String(p.id) === String(id));
};

// Helper retained for external callers — resolves to fallback image when blank.
export const imageUrl = (raw) => {
  if (!raw) return FALLBACK_IMAGE;
  const v = String(raw).trim();
  if (!v) return FALLBACK_IMAGE;
  if (/^https?:\/\//i.test(v) || v.startsWith('/')) return v;
  return resolveAsset(v, 'images');
};

// Drive helper kept exported for any legacy imports.
export const driveUrl = (raw) => resolveAsset(raw, 'images');

export { FALLBACK_IMAGE };
